import axios from "axios";
import type {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { jwtService } from "@/services/jwt";
import { jwtLifecycleService } from "@/services/jwtLifecycleService";
import { errorRecoveryService } from "@/services/errorRecoveryService";
import { configService } from "@/services/configService";
import router from "@/router";
import { notificationService } from "@/services/notificationService";
import {
  reportError,
  reportMessage,
  addRequestBreadcrumb,
} from "@/utils/sentry";

// API错误响应类型
interface ApiErrorResponse {
  message?: string;
  detail?: string;
  [key: string]: unknown;
}

// 请求统计和监控
let activeRequests = 0;
let requestCounter = 0;

// 创建API实例的函数
export function createAPI(baseURL: string) {
  const securityConfig = configService.getSecurityConfig();

  const api = axios.create({
    baseURL: baseURL,
    timeout: securityConfig.apiTimeout,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // 请求拦截器
  api.interceptors.request.use(
    async (config) => {
      const requestId = ++requestCounter;
      activeRequests++;

      // 在请求头中添加请求ID以便追踪
      config.headers = config.headers || {};
      config.headers["X-Request-ID"] = requestId.toString();

      // 添加请求面包屑
      addRequestBreadcrumb(config, "request");

      // 确保URL以斜杠结尾
      if (config.url && !config.url.endsWith("/")) {
        config.url = `${config.url}/`;
      }

      // 智能token管理和主动刷新 - 修复开发环境协议不匹配问题
      const shouldDisableVerification = jwtService.shouldDisableVerification();

      try {
        // 检查并确保token有效性（在协议不匹配时跳过服务端验证）
        const hasValidTokens = shouldDisableVerification
          ? jwtService.hasValidTokens() // 开发环境：只做本地检查
          : await jwtService.ensureValidTokens(); // 生产环境：完整验证

        if (hasValidTokens) {
          const token = jwtService.getToken();
          if (token) {
            // 重要修复：无论协议是否匹配，都要添加Authorization头
            config.headers.Authorization = `Bearer ${token}`;

            // 通知JWT生命周期服务有活动请求
            jwtLifecycleService.notifyActivity();

            if (shouldDisableVerification) {
              console.log(
                `🔧 Added Authorization header in dev mode (protocol mismatch)`,
              );
            }
          }
        } else {
          // 没有有效token，根据请求类型决定处理方式
          const isPublicEndpoint =
            config.url?.includes("/auth/") ||
            config.url?.includes("/public/") ||
            config.url?.includes("/status/");

          if (!isPublicEndpoint) {
            console.warn(
              `No valid tokens available for request: ${config.method?.toUpperCase()} ${config.url}`,
            );
            // 对于需要认证的请求，如果没有token则可能需要重定向到登录页
            // 但这里我们让请求继续，让响应拦截器处理401错误
          }
        }
      } catch (error) {
        console.error("Token validation failed in request interceptor:", error);
        reportError(error as Error, {
          type: "token_validation_error",
          context: "request_interceptor",
          url: config.url,
          method: config.method,
        });
      }

      return config;
    },
    (error) => {
      activeRequests--;
      return Promise.reject(error);
    },
  );

  // 统一的响应拦截器
  api.interceptors.response.use(
    (response) => {
      activeRequests--;

      // 添加响应面包屑
      addRequestBreadcrumb(response, "response");

      // 检查响应中是否有新的token信息
      const newToken =
        response.headers["x-new-token"] || response.headers["X-New-Token"];
      if (newToken) {
        console.log("Received new token from server");
        jwtService.setTokens(newToken, jwtService.getRefreshToken() || "");
      }

      return response;
    },
    async (error: AxiosError) => {
      activeRequests--;

      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
        _retryCount?: number;
      };

      // 添加错误面包屑
      if (error.response) {
        addRequestBreadcrumb(error.response, "error");
      } else {
        addRequestBreadcrumb(
          {
            url: originalRequest?.url,
            method: originalRequest?.method,
            message: error.message,
          },
          "error",
        );
      }

      // 对所有非200响应，第一时间上报到Sentry
      if (error.response) {
        // 立即上报所有API错误到Sentry，包括401错误
        reportError(error, {
          type: "api_error",
          url: originalRequest?.url,
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          method: originalRequest?.method,
          requestId: originalRequest?.headers?.["X-Request-ID"],
        });

        // 处理 401 错误（token 过期或无效）- 增强版
        if (error.response.status === 401) {
          return await handle401Error(api, originalRequest, error);
        }

        // 处理其他错误状态码
        await handleOtherApiErrors(error);
      } else {
        // 网络错误处理
        await handleNetworkError(error, originalRequest);
      }

      return Promise.reject(error);
    },
  );

  return api;
}

// 增强版401错误处理
async function handle401Error(
  api: AxiosInstance,
  originalRequest: InternalAxiosRequestConfig & {
    _retry?: boolean;
    _retryCount?: number;
  },
  error: AxiosError,
): Promise<AxiosResponse> {
  // 防止无限重试
  if (
    originalRequest._retry ||
    (originalRequest._retryCount && originalRequest._retryCount >= 2)
  ) {
    console.warn("Max retry attempts reached for 401 error");
    await handleAuthenticationFailure();
    return Promise.reject(error);
  }

  originalRequest._retry = true;
  originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;

  try {
    console.log(
      `Attempting token refresh (attempt ${originalRequest._retryCount})`,
    );

    // 尝试刷新token
    const refreshed = await jwtService.ensureValidTokens();

    if (refreshed) {
      // 获取新token并重试请求
      const newToken = jwtService.getToken();
      if (newToken) {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        console.log("Token refreshed successfully, retrying request");
        reportMessage("Token自动刷新成功", "info", {
          url: originalRequest.url,
          attempt: originalRequest._retryCount,
        });

        return api(originalRequest);
      }
    }

    // refresh失败
    console.error("Token refresh failed");
    await handleAuthenticationFailure();
    return Promise.reject(error);
  } catch (refreshError) {
    console.error("Error during token refresh:", refreshError);
    reportError(refreshError as Error, {
      type: "token_refresh_error",
      originalUrl: originalRequest.url,
      attempt: originalRequest._retryCount,
    });

    await handleAuthenticationFailure();
    return Promise.reject(error);
  }
}

// 认证失败处理
async function handleAuthenticationFailure(): Promise<void> {
  try {
    // 清除所有认证状态
    jwtService.clearTokens();
    jwtLifecycleService.stop();

    // 检查是否需要错误恢复
    if (errorRecoveryService.detectTrappedUser()) {
      console.warn("Trapped user detected during authentication failure");
      await errorRecoveryService.forceRecovery({
        reason: "authentication_failure",
        redirectPath: "/login",
      });
    } else {
      // 正常跳转到登录页
      router.push("/login");
    }

    reportMessage("认证失败，已清除状态并重定向到登录页", "warning");
  } catch (error) {
    console.error("Error in authentication failure handler:", error);
    reportError(error as Error, {
      type: "auth_failure_handler_error",
    });

    // 最后的兜底
    window.location.href = "/login";
  }
}

// 处理其他API错误
async function handleOtherApiErrors(error: AxiosError): Promise<void> {
  const status = error.response?.status;
  const errorData = error.response?.data as ApiErrorResponse;

  switch (status) {
    case 403:
      notificationService.error("common.permissionError", {
        key: "api-permission-error",
      });
      reportMessage(`权限错误: ${errorData?.message || "权限不足"}`, "warning");
      break;

    case 404:
      notificationService.error("common.resourceNotFound", {
        key: "api-resource-not-found",
      });
      break;

    case 429:
      notificationService.error("common.tooManyRequests", {
        key: "api-rate-limit",
      });
      reportMessage("API请求频率限制", "warning", {
        url: error.config?.url,
      });
      break;

    case 500:
      notificationService.error("common.serverError", {
        key: "api-server-error",
      });
      break;

    case 502:
    case 503:
    case 504:
      notificationService.error("common.serviceUnavailable", {
        key: "api-service-unavailable",
      });
      break;

    case 520:
      // 520 错误，跳转到邮箱设置页面
      router.push("/email-setup");
      break;

    default: {
      // 显示通用错误消息
      const errorMessage =
        errorData?.message || errorData?.detail || "请求失败";
      notificationService.error("common.systemError", {
        key: "api-generic-error",
      });

      // 记录用户看到的错误消息
      reportMessage(`API错误: ${errorMessage}`, "warning", {
        status,
        url: error.config?.url,
      });
      break;
    }
  }
}

// 处理网络错误
async function handleNetworkError(
  error: AxiosError,
  originalRequest?: InternalAxiosRequestConfig,
): Promise<void> {
  console.error("Network error:", error.message);

  reportError(error, {
    type: "network_error",
    url: originalRequest?.url,
    method: originalRequest?.method,
  });

  // 判断错误类型并给出相应提示
  if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
    notificationService.error("common.requestTimeout", {
      key: "network-timeout",
    });
  } else if (error.message.includes("Network Error")) {
    notificationService.error("common.networkError", { key: "network-error" });
  } else {
    notificationService.error("common.unknownError", {
      key: "network-unknown",
    });
  }

  // 检查是否需要错误恢复
  if (activeRequests === 0 && errorRecoveryService.detectTrappedUser()) {
    console.warn(
      "No active requests and trapped user detected after network error",
    );
    await errorRecoveryService.forceRecovery({
      reason: "network_error_recovery",
      redirectPath: "/login",
      showMessage: false,
    });
  }
}

// API统计和监控函数
export function getApiStats() {
  return {
    activeRequests,
    totalRequests: requestCounter,
    timestamp: new Date().toISOString(),
  };
}

// 开发环境使用代理路径，生产环境使用完整URL
const apiBaseURL = import.meta.env.DEV
  ? "/api"
  : import.meta.env.VITE_API_BASE_URL;
export const api = createAPI(apiBaseURL);
